/**
 * Region schedule (desktop/tablet) — one full-width calendar of everyone's
 * workshops in the teacher's region, with their own picked out.
 *
 * Below 1280px the calendar opens on Week (same rule as My schedule's own
 * calendar) — a Month grid is too dense to read at tablet width.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '../../../../lib/useMediaQuery';
import {
  initialCalendarChoices,
  InertWorkshopChip,
  SubregionLegend,
  SubregionPill,
  useCalendarState,
  useSubregionFilter,
  WorkshopsCalendar,
  WorkshopsCalendarChip,
} from '../../primitives';
import type { RenderWorkshopSlot, WorkshopChip } from '../../primitives';
import type { CalendarView } from '../../primitives/workshopsCalendarTypes';
import { BucketError } from '../../regional/BucketStates';
import { teacherChipDetailUrl } from '../../workshopDetailUrlFor';
import { regionScheduleCountLine } from '../region-schedule-count-line';
import { regionScheduleErrorMessage } from '../region-schedule-error';
import { useFillViewport } from '../use-fill-viewport';
import { useRegionScheduleScope } from '../use-region-schedule-scope';
import { useRegionWorkshops } from '../use-region-workshops';

/** Where the calendar switches to opening on the week rather than the month —
 *  matches My schedule's own calendar. */
const NARROW = '(max-width: 1279px)';
/** Kept clear below the calendar — see My schedule's own calendar. */
const PAGE_BOTTOM_PX = 30;
const VIEWS: CalendarView[] = ['Month', 'Week'];
const NO_WORKSHOPS: WorkshopChip[] = [];

const renderWorkshop: RenderWorkshopSlot = ({ workshop, view, compact, onWorkshopClick }) =>
  workshop.mine ? (
    <WorkshopsCalendarChip
      workshop={workshop}
      view={view}
      compact={compact}
      onWorkshopClick={onWorkshopClick}
    />
  ) : (
    <InertWorkshopChip workshop={workshop} view={view} compact={compact} />
  );

export default function TeacherRegionScheduleDesktop() {
  const navigate = useNavigate();
  const narrow = useMediaQuery(NARROW);
  const fill = useFillViewport<HTMLDivElement>(PAGE_BOTTOM_PX);
  const { regions, current } = useRegionScheduleScope();

  const choices = useState(() =>
    initialCalendarChoices({ views: VIEWS, initialView: narrow ? 'Week' : 'Month' }),
  );
  const calendar = useCalendarState(choices);
  const q = useRegionWorkshops(current?.id, calendar.range.from, calendar.range.to);
  const filter = useSubregionFilter(q.data ?? NO_WORKSHOPS, calendar.hidden);

  if (regions.length === 0) {
    return (
      <p className="nb-note" style={{ padding: 16 }}>
        You aren&rsquo;t assigned to any region yet — ask a Regional Director to add you.
      </p>
    );
  }
  if (q.isError) return <BucketError error={new Error(regionScheduleErrorMessage(q.error))} />;

  return (
    <div
      ref={fill.ref}
      style={{ minHeight: fill.height, display: 'flex', flexDirection: 'column' }}
    >
      <WorkshopsCalendar
        workshops={filter.shown}
        views={VIEWS}
        view={calendar.view}
        onViewChange={calendar.setView}
        anchor={calendar.anchor}
        onNav={calendar.nav}
        showCancelled={calendar.showCancelled}
        onToggleCancelled={calendar.toggleCancelled}
        filters={
          <SubregionLegend>
            {filter.subregions.map((s) => (
              <SubregionPill
                key={s.label}
                label={s.label}
                color={s.color}
                hidden={filter.hidden.has(s.label)}
                onToggle={() => calendar.toggleSubregion(s.label)}
              />
            ))}
          </SubregionLegend>
        }
        title="Region calendar"
        fill
        countLineFor={(inPeriod) =>
          q.data ? regionScheduleCountLine(inPeriod, current?.name ?? 'region') : 'Loading…'
        }
        renderWorkshop={renderWorkshop}
        onWorkshopClick={(w) => navigate(teacherChipDetailUrl(w))}
      />
    </div>
  );
}
